import { useState } from "react";
import { useElementWidth } from "../../hooks/useElementWidth";
import { formatIDR, formatIDRCompact } from "../../utils/format";
import { cn } from "../../utils/cn";

/**
 * Acquisition cost and book value by month-end: measured up to this month,
 * a dashed FORECAST after it (today's assets depreciating on schedule; no new
 * purchases, no disposals). Straight segments, no point markers, three or four
 * faint gridlines, an integer-friendly y scale that starts at zero.
 *
 * Hover (or arrow keys, once focused) moves a rule along the months and shows
 * the exact figures for that month.
 */

const BOOK = "#1d5fbf"; // the one data hue, on the main series
const COST = "#94a3b8";
const GRID = "#e5e7eb";
const AXIS_TEXT = "#64748b";
const HEIGHT = 232;
const PAD = { top: 22, right: 14, bottom: 26, left: 62 };

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const shortMonth = (ym) => `${MONTH_NAMES[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;
const longMonth = (ym) => `${MONTH_LONG[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

/** A round axis maximum above `max`, and the step between ticks (4 intervals). */
function niceScale(max) {
  if (!max || max <= 0) return { top: 4, step: 1 };
  const rough = max / 4;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s * 4 >= max * 1.0001) ?? 10 * pow;
  return { top: step * 4, step };
}

export function TrendChart({ months, now, className }) {
  const [ref, width] = useElementWidth();
  const [active, setActive] = useState(null); // index into months, or null

  if (!months?.length) return null;

  const nowIndex = Math.max(0, months.findIndex((m) => m.month === now));
  const max = Math.max(...months.map((m) => Math.max(m.cost, m.bookValue)));
  const { top, step } = niceScale(max);

  const w = Math.max(0, width);
  const plotW = Math.max(1, w - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i) => PAD.left + (months.length === 1 ? 0 : (i / (months.length - 1)) * plotW);
  const y = (v) => PAD.top + plotH - (v / top) * plotH;

  const path = (key, from, to) =>
    months
      .slice(from, to + 1)
      .map((m, k) => `${k === 0 ? "M" : "L"}${x(from + k).toFixed(1)},${y(m[key]).toFixed(1)}`)
      .join(" ");

  const ticks = [0, 1, 2, 3, 4].map((k) => k * step);
  // at most about seven month labels, and always one on "now"
  // a label every N months: at most about seven, and never closer than ~64px
  const pxPerMonth = plotW / Math.max(1, months.length - 1);
  const labelEvery = Math.max(1, Math.ceil(months.length / 7), Math.ceil(64 / pxPerMonth));
  const labelled = months
    .map((m, i) => ({ m, i }))
    .filter(({ i }) => (i - nowIndex) % labelEvery === 0 || i === 0 || i === months.length - 1);
  // The ends of the axis are labelled too, anchored inward so they are never
  // clipped; nothing else is allowed so close to an end that it would collide.
  const labelAnchor = (i) => (i === 0 ? "start" : i === months.length - 1 ? "end" : "middle");
  const labelX = (i) => (i === 0 ? PAD.left : i === months.length - 1 ? w - PAD.right : x(i));
  const labelFits = (i) => i === 0 || i === months.length - 1 || (x(i) - PAD.left > 60 && w - PAD.right - x(i) > 60);
  const axisLabels = labelled.filter(({ i }) => labelFits(i));

  const move = (clientX, rect) => {
    const rel = (clientX - rect.left - PAD.left) / plotW;
    setActive(Math.min(months.length - 1, Math.max(0, Math.round(rel * (months.length - 1)))));
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); setActive((a) => Math.min(months.length - 1, (a ?? nowIndex) + 1)); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); setActive((a) => Math.max(0, (a ?? nowIndex) - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(months.length - 1); }
    else if (e.key === "Escape") setActive(null);
  };

  const point = active === null ? null : months[active];
  const tipLeft = point ? Math.min(w - 8, Math.max(8, x(active))) : 0;
  const tipOnRight = point && x(active) < w * 0.6;

  return (
    <div className={cn("relative", className)}>
      <div ref={ref} className="relative" style={{ height: HEIGHT }}>
        {w > 0 && (
          <svg
            width={w}
            height={HEIGHT}
            role="img"
            tabIndex={0}
            aria-label={`Acquisition cost and book value by month, from ${longMonth(months[0].month)} to ${longMonth(months[months.length - 1].month)}. Use the left and right arrow keys to read each month.`}
            onPointerMove={(e) => move(e.clientX, e.currentTarget.getBoundingClientRect())}
            onPointerLeave={() => setActive(null)}
            onKeyDown={onKeyDown}
            onFocus={() => setActive((a) => a ?? nowIndex)}
            onBlur={() => setActive(null)}
            className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
          >
            {/* the forecast stretch is tinted so it never reads as measured */}
            <rect x={x(nowIndex)} y={PAD.top} width={Math.max(0, w - PAD.right - x(nowIndex))} height={plotH} fill="#f8fafc" />

            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={w - PAD.right} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth="1" />
                <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={AXIS_TEXT}>
                  {t === 0 ? "0" : formatIDRCompact(t)}
                </text>
              </g>
            ))}

            {axisLabels.map(({ m, i }) => (
              <text key={m.month} x={labelX(i)} y={HEIGHT - 8} textAnchor={labelAnchor(i)} fontSize="11" fill={AXIS_TEXT}>
                {shortMonth(m.month)}
              </text>
            ))}

            {/* cost: grey, behind. book value: the main series. dashed once forecast */}
            <path d={path("cost", 0, nowIndex)} fill="none" stroke={COST} strokeWidth="1.5" />
            <path d={path("cost", nowIndex, months.length - 1)} fill="none" stroke={COST} strokeWidth="1.5" strokeDasharray="4 4" />
            <path d={path("bookValue", 0, nowIndex)} fill="none" stroke={BOOK} strokeWidth="2" />
            <path d={path("bookValue", nowIndex, months.length - 1)} fill="none" stroke={BOOK} strokeWidth="2" strokeDasharray="5 4" />

            {/* "Now": a 1px rule with its label above the plot, clear of both lines */}
            <line x1={x(nowIndex)} x2={x(nowIndex)} y1={PAD.top - 6} y2={PAD.top + plotH} stroke="#0f172a" strokeWidth="1" />
            <text x={x(nowIndex) + (nowIndex > months.length * 0.7 ? -6 : 6)} y={PAD.top - 9} textAnchor={nowIndex > months.length * 0.7 ? "end" : "start"} fontSize="11" fontWeight="500" fill="#0f172a">
              Now
            </text>

            {point && (
              <g pointerEvents="none">
                <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + plotH} stroke="#475569" strokeWidth="1" strokeDasharray="2 3" />
                <circle cx={x(active)} cy={y(point.cost)} r="3.5" fill="#fff" stroke={COST} strokeWidth="2" />
                <circle cx={x(active)} cy={y(point.bookValue)} r="4" fill="#fff" stroke={BOOK} strokeWidth="2" />
              </g>
            )}
          </svg>
        )}

        {point && (
          <div
            role="status"
            className="pointer-events-none absolute z-10 w-52 rounded-md bg-slate-900 px-3 py-2 text-xs text-white shadow-lg"
            style={{
              top: 4,
              left: tipOnRight ? tipLeft + 12 : undefined,
              right: tipOnRight ? undefined : Math.max(0, w - tipLeft) + 12,
            }}
          >
            <p className="font-medium">
              {longMonth(point.month)}
              {point.forecast && <span className="ml-1.5 text-slate-300 font-normal">forecast</span>}
            </p>
            <p className="mt-1 flex justify-between gap-3">
              <span className="text-slate-300">Book value</span>
              <span className="tabular-nums">{formatIDR(point.bookValue)}</span>
            </p>
            <p className="flex justify-between gap-3">
              <span className="text-slate-300">Acquisition cost</span>
              <span className="tabular-nums">{formatIDR(point.cost)}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
